// =============================================================================
// Auth Store - Gestione Stato Autenticazione Multi-Tenant
// =============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiClient } from '../api/client';
import i18n from '../i18n';

// =============================================================================
// Types
// =============================================================================

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  isActive: boolean;
}

export interface TenantMembership {
  organizationId: string;
  tenantName: string;
  tenantSlug: string;
  /// Timezone IANA dell'organizzazione, usato per rendere tutti gli orari.
  timezone?: string;
  role: 'OWNER' | 'ADMIN' | 'WORKER';
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
  memberships: TenantMembership[];
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface AuthState {
  // State
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  memberships: TenantMembership[];

  // Actions
  login: (email: string, password: string) => Promise<TenantMembership[]>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => void;
  updateSession: (patch: { user?: User; token?: string }) => void;
  verifyToken: () => Promise<boolean>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

// =============================================================================
// Store
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial State
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      memberships: [],

      // Login - restituisce memberships per il routing
      login: async (email: string, password: string): Promise<TenantMembership[]> => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiClient.post<LoginResponse>('/auth/login', {
            email,
            password,
          });

          const { access_token, user, memberships } = response.data;

          // Update state
          set({
            token: access_token,
            user,
            memberships,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Update axios default header
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          return memberships;
        } catch (error: any) {
          const message =
            error.response?.data?.message ||
            error.message ||
            i18n.t('loginError', { ns: 'auth' });

          set({
            token: null,
            user: null,
            memberships: [],
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });

          return [];
        }
      },

      // Register - il backend risponde come il login, quindi l'utente entra
      // subito autenticato (memberships vuote → onboarding)
      register: async (data: RegisterData): Promise<boolean> => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiClient.post<LoginResponse>('/auth/register', data);

          const { access_token, user, memberships } = response.data;

          set({
            token: access_token,
            user,
            memberships,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          return true;
        } catch (error: any) {
          const message =
            error.response?.data?.message ||
            error.message ||
            i18n.t('register.error', { ns: 'auth' });

          set({
            isLoading: false,
            error: typeof message === 'string' ? message : message[0],
          });

          return false;
        }
      },

      // Aggiorna profilo e/o token dopo una modifica dall'area account.
      // Il token nuovo arriva dal backend quando cambia l'email (il payload
      // portava l'indirizzo vecchio) o la password (che revoca i token
      // precedenti, questa sessione compresa).
      updateSession: ({ user, token }) => {
        if (token) {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        set((state) => ({
          user: user ?? state.user,
          token: token ?? state.token,
        }));
      },

      // Logout
      logout: () => {
        delete apiClient.defaults.headers.common['Authorization'];
        delete apiClient.defaults.headers.common['x-tenant-id'];

        set({
          token: null,
          user: null,
          memberships: [],
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      // Verify Token
      verifyToken: async (): Promise<boolean> => {
        const { token } = get();

        if (!token) {
          return false;
        }

        set({ isLoading: true });

        try {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          const response = await apiClient.get<{ valid: boolean }>('/auth/verify');

          if (response.data.valid) {
            set({ isAuthenticated: true, isLoading: false });
            return true;
          } else {
            get().logout();
            return false;
          }
        } catch {
          get().logout();
          return false;
        }
      },

      // Clear Error
      clearError: () => {
        set({ error: null });
      },

      // Set Loading
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'skedulab-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        memberships: state.memberships,
      }),
      onRehydrateStorage: () => {
        return (state: AuthState | undefined, error?: unknown) => {
          if (error) {
            console.error('Error rehydrating auth store:', error);
          } else if (state?.token) {
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
          }
        };
      },
    }
  )
);

// =============================================================================
// Selectors
// =============================================================================

export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectUser = (state: AuthState) => state.user;
export const selectToken = (state: AuthState) => state.token;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectError = (state: AuthState) => state.error;
export const selectMemberships = (state: AuthState) => state.memberships;
